/**
 * Accessibility remediation shim for the Judge.me review widget on PDP.
 * Patches Judge.me's same-document DOM after render, re-applied via MutationObserver.
 */
(function () {
  var WIDGET_SELECTOR = '#judgeme_product_reviews';
  var MODAL_SELECTOR = '.jdgm-write-review-modal';
  var STAR_WORDS = ['', '1 star', '2 stars', '3 stars', '4 stars', '5 stars'];

  // ---- Inline widget (review list, pagination, "Write a review" link) ----

  function fixWriteReviewLink(root) {
    var link = root.querySelector('.jdgm-write-rev-link');
    if (!link) return;
    if (link.getAttribute('aria-haspopup') !== 'dialog') {
      link.setAttribute('aria-haspopup', 'dialog');
    }
    if (link.hasAttribute('aria-expanded')) {
      link.removeAttribute('aria-expanded');
    }
  }

  function fixPaginationLabels(root) {
    var map = {
      '.jdgm-paginate__next-page': 'Next page',
      '.jdgm-paginate__last-page': 'Last page',
      '.jdgm-paginate__prev-page': 'Previous page',
      '.jdgm-paginate__first-page': 'First page'
    };
    Object.keys(map).forEach(function (selector) {
      var el = root.querySelector(selector);
      if (el && el.getAttribute('aria-label') !== map[selector]) {
        el.setAttribute('aria-label', map[selector]);
      }
    });
  }

  function fixRatingStarTabindex(root) {
    // role="img" is correctly non-interactive; drop the stray tabindex.
    var ratings = root.querySelectorAll('.jdgm-rev__rating[role="img"][tabindex]');
    ratings.forEach(function (el) {
      el.removeAttribute('tabindex');
    });
  }

  var liveRegion = null;

  function ensureLiveRegion(root) {
    if (liveRegion && root.contains(liveRegion)) return liveRegion;
    liveRegion = root.querySelector('.gz-jdgm-announcer');
    if (liveRegion) return liveRegion;
    liveRegion = document.createElement('div');
    liveRegion.className = 'gz-jdgm-announcer sr-only';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.style.cssText =
      'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
    root.appendChild(liveRegion);
    return liveRegion;
  }

  function announce(root, message) {
    var region = ensureLiveRegion(root);
    // Clear first so repeated identical messages still get announced.
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 50);
  }

  function fixLiveAnnouncements(root) {
    ensureLiveRegion(root);

    var histogramStars = root.querySelectorAll('.jdgm-histogram__star:not([data-gz-bound])');
    histogramStars.forEach(function (el) {
      el.setAttribute('data-gz-bound', 'true');
      el.addEventListener('click', function () {
        var label = el.getAttribute('aria-label') || 'Reviews filtered';
        announce(root, 'Filtered by ' + label + '. Review list updated.');
      });
    });

    var clearFilter = root.querySelector('.jdgm-histogram__clear-filter:not([data-gz-bound])');
    if (clearFilter) {
      clearFilter.setAttribute('data-gz-bound', 'true');
      clearFilter.addEventListener('click', function () {
        announce(root, 'Filter cleared. Showing all reviews.');
      });
    }

    var pageLinks = root.querySelectorAll('.jdgm-paginate__page:not([data-gz-bound])');
    pageLinks.forEach(function (el) {
      el.setAttribute('data-gz-bound', 'true');
      el.addEventListener('click', function () {
        var label = el.getAttribute('aria-label') || 'page';
        window.setTimeout(function () {
          announce(root, 'Showing ' + label + ' of reviews.');
        }, 300);
      });
    });

    var sortSelect = root.querySelector('.jdgm-sort-dropdown:not([data-gz-bound])');
    if (sortSelect) {
      sortSelect.setAttribute('data-gz-bound', 'true');
      sortSelect.addEventListener('change', function () {
        window.setTimeout(function () {
          announce(root, 'Reviews sorted by ' + sortSelect.options[sortSelect.selectedIndex].text + '.');
        }, 300);
      });
    }
  }

  var sortEscapeInterceptorBound = false;

  function fixSortEscapeKey() {
    if (sortEscapeInterceptorBound) return;
    sortEscapeInterceptorBound = true;
    // Capture-phase on document so this runs before Judge.me's own handler on the select.
    document.addEventListener(
      'keydown',
      function (e) {
        if (e.key === 'Escape' && e.target && e.target.classList && e.target.classList.contains('jdgm-sort-dropdown')) {
          e.stopPropagation();
          e.target.blur();
        }
      },
      true
    );
  }

  function applyWidgetFixes(root) {
    fixWriteReviewLink(root);
    fixPaginationLabels(root);
    fixRatingStarTabindex(root);
    fixLiveAnnouncements(root);
    fixSortEscapeKey();
  }

  // ---- "Write a review" modal ----

  function fixStarLabels(modal) {
    var stars = modal.querySelectorAll('[data-alt]');
    stars.forEach(function (star) {
      var n = parseInt(star.getAttribute('data-alt'), 10);
      var correct = STAR_WORDS[n];
      if (correct && star.getAttribute('aria-label') !== correct) {
        star.setAttribute('aria-label', correct);
      }
    });
  }

  function fixProductImageLazyLoad(modal) {
    // loading="lazy" never fires inside a page hidden via display:none, so the
    // image never loads and gets skipped by screen readers. Force eager.
    var img = modal.querySelector('.jdgm-write-review-modal__product-image img[loading="lazy"]');
    if (img) {
      img.loading = 'eager';
    }
  }

  function applyModalFixes(modal) {
    fixStarLabels(modal);
    fixProductImageLazyLoad(modal);
  }

  // ---- Focus trap (shared, works on whichever dialog is currently open) ----

  var focusTrap = { active: false, container: null, keydownHandler: null, lastFocused: null };

  function getFocusable(container) {
    var items = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(items, function (el) {
      // offsetParent misses visibility:hidden, which Judge.me uses for inactive wizard pages.
      if (el.offsetParent === null) return false;
      return getComputedStyle(el).visibility !== 'hidden';
    });
  }

  function trapFocus(container) {
    if (focusTrap.active) return;
    focusTrap.active = true;
    focusTrap.container = container;
    focusTrap.lastFocused = document.activeElement;

    var focusables = getFocusable(container);
    if (focusables.length) focusables[0].focus();

    focusTrap.keydownHandler = function (e) {
      if (e.key === 'Escape') {
        releaseFocusTrap();
        return;
      }
      if (e.key !== 'Tab') return;
      var items = getFocusable(container);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', focusTrap.keydownHandler, true);
  }

  function releaseFocusTrap() {
    if (!focusTrap.active) return;
    document.removeEventListener('keydown', focusTrap.keydownHandler, true);
    if (focusTrap.lastFocused && typeof focusTrap.lastFocused.focus === 'function') {
      focusTrap.lastFocused.focus();
    }
    focusTrap.active = false;
    focusTrap.container = null;
    focusTrap.keydownHandler = null;
    focusTrap.lastFocused = null;
  }

  function watchModalVisibility(modal) {
    var visible = getComputedStyle(modal).display !== 'none';
    if (visible && !focusTrap.active) {
      trapFocus(modal);
    } else if (!visible && focusTrap.active && focusTrap.container === modal) {
      releaseFocusTrap();
    }
  }

  // ---- Init ----

  function attachModalObserver(modal) {
    applyModalFixes(modal);
    watchModalVisibility(modal);
    new MutationObserver(function () {
      applyModalFixes(modal);
      watchModalVisibility(modal);
    }).observe(modal, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'aria-label'] });
  }

  function init() {
    var root = document.querySelector(WIDGET_SELECTOR);
    if (root) {
      applyWidgetFixes(root);
      new MutationObserver(function () {
        applyWidgetFixes(root);
      }).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
    }

    // Modal is lazy-inserted on first "Write a review" click, watch <body> until it appears.
    var modal = document.querySelector(MODAL_SELECTOR);
    if (modal) {
      attachModalObserver(modal);
    } else {
      var bodyObserver = new MutationObserver(function () {
        var found = document.querySelector(MODAL_SELECTOR);
        if (found) {
          bodyObserver.disconnect();
          attachModalObserver(found);
        }
      });
      bodyObserver.observe(document.body, { childList: true, subtree: false });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
